import { formatDate } from '../../../lib/format';
import { EMPLOYMENT_STATUS_LABEL, EXAM_STATUS_LABEL, EXAM_TYPE_LABEL } from '@dru/types';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import type { OversightCertificate, OversightEmployee, OversightExamination, OversightPlant } from '@dru/types';
import { useState } from 'react';
import { DataTable, EmptyState, IconAction, RowActions, StatusBadge, Tabs, type Tone } from '@dru/ui';
import { Eye, FileText } from 'lucide-react';
import { DocumentViewer } from '../components/DocumentViewer';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth } from '../../../lib/auth';
import { holds } from '../../../layouts/ConsoleShell';

export type Tab = 'plants' | 'employees' | 'examinations' | 'certificates';
export const OVERSIGHT_TABS: Array<{ key: Tab; label: string; need: string }> = [
  { key: 'plants', label: 'Plants', need: 'crosstenant.plant.read' },
  { key: 'employees', label: 'Workforce', need: 'crosstenant.employee.read' },
  { key: 'examinations', label: 'Examinations', need: 'crosstenant.examination.read' },
  { key: 'certificates', label: 'Certificates', need: 'crosstenant.certificate.read' },
];

/** Keep old bookmarks working without a separate access screen. */
export function Oversight() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const recordTab = params.get('tab');
  // Each kind of record is its own tab on the company page now.
  const known = OVERSIGHT_TABS.some(t => t.key === recordTab);
  const next = new URLSearchParams({ tab: known ? (recordTab === 'employees' ? 'workforce' : recordTab!) : 'workforce' });
  return <Navigate to={`/admin/companies/${id}?${next}`} replace />;
}

/** Permission-filtered records within the company profile; each fetch is audited. */
export function CompanyRecords({ tenantId }: { tenantId: string }) {
  const { permissions } = useAuth();
  const [params, setParams] = useSearchParams();
  const tabs = OVERSIGHT_TABS.filter(t => holds(permissions, t.need));
  const active = tabs.find(t => t.key === params.get('records'))?.key ?? tabs[0]?.key;
  if (!active) return <EmptyState title="Records are not available to your role" />;
  return <div className="space-y-4">
    <Tabs tabs={tabs} active={active} onChange={key => {
      const next = new URLSearchParams(params); next.set('records', key);
      next.delete('reason'); next.delete('ticketRef'); setParams(next, { replace: true });
    }} />
    <Panel key={`${tenantId}:${active}`} tenantId={tenantId} tab={active} />
  </div>;
}

/** One tab, one request, one audit entry. */
export function Panel({ tenantId, tab }: { tenantId: string; tab: Tab }) {
  const qs = 'limit=100';
  const q = useQuery<{ items: any[] }>(
    () => api.get(`/platform/tenants/${tenantId}/${tab}?${qs}`),
    [tenantId, tab],
  );
  const rows = q.data?.items ?? [];
  // The PDFs behind a row, in a dialog; the server checks the same key.
  const { permissions } = useAuth();
  const canView = holds(permissions, 'certificate.download');
  const [viewing, setViewing] = useState<{ title: string; subtitle: string; path: string; file: string } | null>(null);
  const viewer = viewing && (
    <DocumentViewer title={viewing.title} subtitle={viewing.subtitle} basePath={viewing.path}
      fileName={viewing.file} onClose={() => setViewing(null)} />
  );
  const common = {
    loading: q.loading, error: q.error, onRetry: q.reload,
  };

  if (tab === 'plants') {
    return (
      <DataTable<OversightPlant>
        {...common} rows={rows} rowKey={(p) => p.id}
        empty={{ title: 'No plants', hint: 'This company has not set up its sites yet.' }}
        columns={[
          { key: 'name', header: 'Plant', alwaysVisible: true, cell: (p) => (
            <div>
              <div className="text-body text-neutral-900">{p.name}</div>
              <div className="font-mono text-caption text-neutral-500">{p.code}</div>
            </div>
          ) },
          { key: 'where', header: 'Location', cell: (p) => (
            <span className="text-small text-neutral-700">{[p.city, p.state].filter(Boolean).join(', ') || 'Not recorded'}</span>
          ) },
          { key: 'employees', header: 'Employees', align: 'right', cell: (p) => <span className="tabular-nums">{p.employees}</span> },
          { key: 'status', header: 'Status', mobileLabel: false, cell: (p) => (
            <StatusBadge tone={p.status === 'ACTIVE' ? 'covered' : 'neutral'}>{sentence(p.status)}</StatusBadge>
          ) },
        ]}
      />
    );
  }

  if (tab === 'employees') {
    return (
      <DataTable<OversightEmployee>
        {...common} rows={rows} rowKey={(e) => e.id}
        empty={{ title: 'No employees', hint: 'Nobody has been added to this company yet.' }}
        columns={[
          { key: 'code', header: 'Employee', alwaysVisible: true, cell: (e) => (
            <div>
              <div className="text-body text-neutral-900">{e.fullName}</div>
              <div className="font-mono text-caption text-neutral-500">{e.employeeCode}</div>
            </div>
          ) },
          { key: 'plant', header: 'Plant', cell: (e) => e.plant?.name ?? <span className="text-neutral-500">None</span> },
          { key: 'role', header: 'Job role', cell: (e) => e.jobRole?.title ?? <span className="text-neutral-500">None</span> },
          { key: 'status', header: 'Status', mobileLabel: false, cell: (e) => (
            <StatusBadge tone={e.status === 'ACTIVE' ? 'covered' : 'neutral'}>
              {EMPLOYMENT_STATUS_LABEL[e.status as keyof typeof EMPLOYMENT_STATUS_LABEL] ?? sentence(e.status)}
            </StatusBadge>
          ) },
        ]}
      />
    );
  }

  if (tab === 'examinations') {
    return (
      <>
      <DataTable<OversightExamination>
        {...common} rows={rows} rowKey={(x) => x.id}
        empty={{ title: 'No examinations', hint: 'Nothing has been raised for this company yet.' }}
        columns={[
          // By employee code only, as before: a name beside a medical case is
          // more than an oversight read needs.
          { key: 'worker', header: 'Employee', alwaysVisible: true, cell: (x) => (
            <span className="font-mono text-small text-neutral-800">{x.employee.employeeCode}</span>
          ) },
          { key: 'type', header: 'Type', cell: (x) => (
            <span className="text-small text-neutral-700">{(EXAM_TYPE_LABEL[x.examinationType] ?? sentence(x.examinationType))}</span>
          ) },
          { key: 'status', header: 'Stage', cell: (x) => (
            <StatusBadge tone="neutral">{EXAM_STATUS_LABEL[x.status as keyof typeof EXAM_STATUS_LABEL] ?? sentence(x.status)}</StatusBadge>
          ) },
          { key: 'due', header: 'Due', cell: (x) => fmt(x.dueDate) },
          { key: 'sla', header: 'Turnaround', cell: (x) => (
            x.slaBreachedAt
              ? <StatusBadge tone="overdue">Past due</StatusBadge>
              : <span className="text-small text-neutral-600">{x.slaDueAt ? `Due ${fmt(x.slaDueAt)}` : 'No target'}</span>
          ) },
          ...(canView ? [{ key: 'go', header: 'Actions', align: 'right' as const, alwaysVisible: true, mobileLabel: '', cell: (x: OversightExamination) => {
            // Paper exists once the physician has signed: certified, or since lapsed.
            const signed = x.status === 'CERTIFIED' || x.status === 'EXPIRED';
            return (
              <RowActions>
                <IconAction
                  label={signed ? `View the report for ${x.employee.employeeCode}` : 'No report yet: issued once the physician signs'}
                  icon={FileText} disabled={!signed}
                  onClick={() => setViewing({
                    title: 'Examination report', subtitle: `${x.employee.employeeCode} · ${EXAM_TYPE_LABEL[x.examinationType] ?? sentence(x.examinationType)}`,
                    path: `/platform/tenants/${tenantId}/examinations/${x.id}`, file: `report-${x.employee.employeeCode}`,
                  })}
                />
              </RowActions>
            );
          } }] : []),
        ]}
      />
      {viewer}
      </>
    );
  }

  return (
    <>
    <DataTable<OversightCertificate>
      {...common} rows={rows} rowKey={(c) => c.id}
      empty={{ title: 'No certificates', hint: 'None have been issued for this company yet.' }}
      columns={[
        { key: 'number', header: 'Certificate', alwaysVisible: true, cell: (c) => (
          <div>
            <div className="font-mono text-small text-neutral-900">{c.certificateNumber}</div>
            <div className="font-mono text-caption text-neutral-500">{c.employee.employeeCode}</div>
          </div>
        ) },
        { key: 'status', header: 'Status', cell: (c) => (
          <StatusBadge tone={certTone(c)}>{certWord(c)}</StatusBadge>
        ) },
        { key: 'valid', header: 'Valid', cell: (c) => (
          <span className="text-small text-neutral-600">{fmt(c.validFrom)} to {fmt(c.validUntil)}</span>
        ) },
        { key: 'doc', header: 'Document', cell: (c) => (
          c.generationStatus === 'ISSUED'
            ? <span className="text-small text-neutral-600">Issued</span>
            : <StatusBadge tone={c.generationStatus === 'GENERATION_FAILED' ? 'danger' : 'pending'}>{sentence(c.generationStatus)}</StatusBadge>
        ) },
        { key: 'revoked', header: 'Revoked', hideOnMobile: true, cell: (c) => (
          c.revokedAt
            ? <span className="text-small text-danger-600">{fmt(c.revokedAt)} · {c.revokedReason ?? 'No reason given'}</span>
            : <span className="text-small text-neutral-500">No</span>
        ) },
        ...(canView ? [{ key: 'go', header: 'Actions', align: 'right' as const, alwaysVisible: true, mobileLabel: '', cell: (c: OversightCertificate) => (
          <RowActions>
            <IconAction
              label={`View certificate ${c.certificateNumber}`} icon={Eye}
              onClick={() => setViewing({
                title: 'Fitness certificate', subtitle: `${c.certificateNumber} · ${c.employee.employeeCode}`,
                path: `/platform/tenants/${tenantId}/certificates/${c.id}`, file: c.certificateNumber,
              })}
            />
          </RowActions>
        ) }] : []),
      ]}
    />
    {viewer}
    </>
  );
}

/**
 * A certificate's registry state. Revocation is an administrative act on the
 * document, not a fitness outcome, so it takes the danger tone rather than
 * the clinical "unfit" one.
 */
function certTone(c: OversightCertificate): Tone {
  if (c.status === 'REVOKED') return 'danger';
  if (c.status !== 'ACTIVE') return 'neutral';
  return new Date(c.validUntil) < new Date() ? 'overdue' : 'covered';
}

function certWord(c: OversightCertificate): string {
  if (c.status === 'ACTIVE' && new Date(c.validUntil) < new Date()) return 'Lapsed';
  return sentence(c.status);
}

/** An enum value as a reader would write it: GENERATION_FAILED → "Generation failed". */
function sentence(v: string): string {
  const s = v.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const fmt = (d: string | null) => (d ? formatDate(d) : '—');
